import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Stack, TextField } from "@fluentui/react";
import { Send28Filled } from "@fluentui/react-icons";

import styles from "./QuestionInput.module.css";

interface Props {
  onSend: (question: string) => void;
  disabled: boolean;
  placeholder?: string;
  clearOnSend?: boolean;
  copiedQuestion?: string;
  setCopiedQuestion?: Dispatch<SetStateAction<string>>;
  isReplay?: boolean;
  setIsReplay?: Dispatch<SetStateAction<boolean>>;
}

export const QuestionInput = ({
  copiedQuestion = "",
  isReplay,
  setIsReplay,
  setCopiedQuestion,
  onSend,
  disabled,
  placeholder,
  clearOnSend,
}: Props) => {
  const [question, setQuestion] = useState<string>("");

  useEffect(() => {
    if (copiedQuestion) {
      setQuestion(copiedQuestion);
      setCopiedQuestion?.("");

      if (isReplay) {
        onSend(copiedQuestion);
        setIsReplay?.(false);
        setCopiedQuestion?.("");
        setQuestion("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copiedQuestion, isReplay]);

  const sendQuestion = () => {
    if (disabled || !question.trim()) {
      return;
    }

    onSend(question);

    if (clearOnSend) {
      setQuestion("");
    }
  };

  const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
    if (ev.key === "Enter" && !ev.shiftKey) {
      ev.preventDefault();
      sendQuestion();
    }
  };

  const onQuestionChange = (
    _ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) => {
    if (!newValue) {
      setQuestion("");
    } else if (newValue.length <= 1000) {
      setQuestion(newValue);
    }
    setCopiedQuestion?.("");
  };

  const sendQuestionDisabled = disabled || !question.trim();

  return (
    <Stack horizontal className={styles.questionInputContainer}>
      <TextField
        className={styles.questionInputTextArea}
        placeholder={placeholder}
        multiline
        resizable={false}
        borderless
        value={question}
        onChange={onQuestionChange}
        onKeyDown={onEnterPress}
      />
      <div className={styles.questionInputButtonsContainer}>
        <div
          className={`${styles.questionInputSendButton} ${
            sendQuestionDisabled ? styles.questionInputSendButtonDisabled : ""
          }`}
          aria-label="Ask question button"
          onClick={sendQuestion}
        >
          <Send28Filled primaryFill="var(--primary-color)" />
        </div>
      </div>
    </Stack>
  );
};
